import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../app/api';

export default function FrameworksLanding() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes('@')) {
      toast.error('Please enter a valid email.');
      return;
    }
    setSubmitting(true);
    try {
      const params = new URLSearchParams(window.location.search);
      await api.post('/api/leadmagnet/request', { email, name, source: params.get('src') || 'landing' });
      setSent(true);
      // Auto-redirect to the download page after a beat so they actually see the goods
      setTimeout(() => navigate('/frameworks-download'), 1500);
    } catch (e) {
      toast.error(e.message || 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="frameworks-landing">
      <div className="max-w-3xl mx-auto px-4 py-12 sm:py-16">
        <div className="text-center space-y-4 mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#004C54]/10 text-[#004C54] text-xs font-bold uppercase tracking-[0.16em]">
            <Sparkles className="h-3 w-3" /> Free Preview
          </div>
          <h1 className="font-heading text-3xl sm:text-5xl font-bold tracking-tight">
            2 Protocols From <span className="text-[#004C54]">The Apex Six.</span><br className="hidden sm:block" /> Free. Step-by-step.
          </h1>
          <p className="text-foreground/70 text-base sm:text-lg max-w-xl mx-auto">
            The 3-Breath Reset and The ABC Confidence Stack Coach Lewis runs with serious athletes &mdash; written out, ready to use tonight. The other four protocols live in The Locker Room.
          </p>
        </div>

        <Card className="border-2 border-[#004C54]/20">
          <CardContent className="p-6 sm:p-8">
            {!sent ? (
              <form onSubmit={submit} className="space-y-4" data-testid="frameworks-form">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-foreground/60">Your name (optional)</label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jordan" className="mt-1" data-testid="frameworks-name" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-foreground/60">Email</label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" className="mt-1" data-testid="frameworks-email" />
                </div>
                <Button type="submit" disabled={submitting} className="w-full bg-[#004C54] hover:bg-[#003a40] text-white" size="lg" data-testid="frameworks-submit">
                  {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</> : 'Send me the frameworks'}
                </Button>
                <p className="text-xs text-foreground/55 text-center">No spam. Unsubscribe anytime. Weekly mental rep + occasional pep talk.</p>
              </form>
            ) : (
              <div className="text-center py-6 space-y-3" data-testid="frameworks-sent">
                <div className="w-12 h-12 mx-auto rounded-full bg-[#004C54]/10 flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6 text-[#004C54]" />
                </div>
                <div className="font-heading text-xl font-bold">Check your inbox.</div>
                <p className="text-sm text-foreground/70">Opening the playbook now...</p>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-foreground/55 mt-6">
          Already have the playbook? <button onClick={() => navigate('/frameworks-download')} className="underline hover:text-[#004C54]" data-testid="frameworks-skip">Open it →</button>
        </p>

        <div className="mt-10 rounded-2xl border-2 border-[#004C54]/20 bg-[#004C54]/5 p-5 sm:p-6 text-center" data-testid="frameworks-quiz-crosspromo">
          <div className="font-mono-accent uppercase tracking-[0.16em] text-[10px] font-bold text-[#004C54]">Not sure where to start?</div>
          <div className="font-heading text-lg sm:text-xl font-bold mt-1">Take the free 2-min Mindset Quiz</div>
          <p className="text-foreground/70 text-sm mt-1 mb-4">
            Find out which of the 6 frameworks to run first — based on your mental game gap.
          </p>
          <button
            onClick={() => navigate('/quiz')}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#004C54] hover:bg-[#003a40] text-white font-semibold text-sm transition-colors"
            data-testid="frameworks-quiz-cta"
          >
            Take the quiz →
          </button>
        </div>
      </div>
    </div>
  );
}
